/**
 * StyledDialog - Unified dialog component with appearance-based styling.
 *
 * This is the ONE LEVER for dialog styling. Change `appearance` to switch
 * between clean (default, minimal chrome) or minimal (overlay close, no footer).
 *
 * @example
 * // Clean (default) - floating close, inline footer
 * <StyledDialog
 *   title="Edit Profile"
 *   footer={{ variant: "confirm", primaryText: "Save", onPrimary: handleSave }}
 * >
 *   <TextField label="Name" />
 * </StyledDialog>
 *
 * @example
 * // Minimal - just content with overlay close (QrCodeDialog style)
 * <StyledDialog appearance="minimal">
 *   <img src={qrCode} />
 * </StyledDialog>
 *
 * @example
 * // Custom footer with ReactNode (complex button layouts)
 * <StyledDialog
 *   title="Assignment Details"
 *   footer={
 *     <>
 *       <ActionButton action={Actions.save} mode="dialog-primary" ... />
 *       <ActionButton action={Actions.close} mode="dialog-secondary" ... />
 *     </>
 *   }
 * >
 *   {content}
 * </StyledDialog>
 */

import {
  isValidElement,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { Actions } from '@/theme';
import { buttonLabels } from '@/constants';
import { dialogMaxWidths, type DialogSize } from '@/components/templates';
import {
  dialogAppearance,
  type DialogAppearance,
  type DialogAppearanceConfig,
} from '@/components/templates/dialogs/dialogAppearance';
import { ActionButton } from '@/components/common/ActionButton';
import { testIds } from '@/test/testIds';
import { Text, Alert } from '@/components/primitives';
import { wrapTextChild } from '@/components/primitives/wrapTextChild';
import {
  overlay,
  overlayNested,
  overlayDim,
  contentBase,
  contentClean,
  contentMinimal,
  contentOverflowVisible,
  contentCompact,
  innerContentCompact,
  footerCompact,
  innerContent,
  innerContentFit,
  innerContentWithTopPadding,
  footer as footerClass,
  closeButtonWrapper,
  closeButtonOverlayWrapper,
  errorSlot,
  srOnly,
} from './dialog.css';
import { pushOpenDialog } from './dialogStack';
import { DialogLayerProvider, useDialogLayerFeedback } from './DialogLayerContext';

const FLUSH_ALERT_STYLE = { borderRadius: 0, borderLeft: 'none' } as const;

// Larger than the ~15px title text so the title icon reads as the dialog's
// primary glyph rather than an inline character.
const DIALOG_TITLE_ICON_SIZE = 24;

// =============================================================================
// TYPES
// =============================================================================

export interface StyledDialogFooterConfig {
  /** Footer variant determines button styling */
  variant?: 'confirm' | 'destructive' | 'acknowledge';
  /** Primary button text */
  primaryText?: string;
  /** Secondary/cancel button text */
  secondaryText?: string;
  /** Primary button click handler */
  onPrimary?: () => void | Promise<void>;
  /** Secondary button click handler (defaults to onClose) */
  onSecondary?: () => void | Promise<void>;
  /** Loading state */
  loading?: boolean;
  /** Loading text */
  loadingText?: string;
  /** Disable primary button */
  primaryDisabled?: boolean;
  /** Hide secondary button */
  hideSecondary?: boolean;
  /** Icon before primary text */
  primaryIcon?: ReactNode;
}

interface StyledDialogBaseProps {
  /** Whether dialog is open */
  open: boolean;
  /** Close handler. Optional for non-dismissible dialogs with hideClose and closeDisabled. */
  onClose?: () => void;
  /** Visual appearance - the ONE LEVER */
  appearance?: DialogAppearance;
  /** Dialog size preset */
  size?: DialogSize;
  /**
   * Size to use when this dialog is stacked on top of another one.
   *
   * Nesting is detected at runtime by StyledDialog (see `dialogStack.ts`), so
   * the *policy* — "a confirm shrinks when it appears over something" — lives
   * with the component that has an opinion, while the *detection* stays here.
   * Omit to keep `size` regardless of stacking.
   */
  nestedSize?: DialogSize;
  /**
   * Opt-in. Make the body a flex column inside the 90vh cap so a child can scroll on its own
   * (`flex: 1 1 auto; min-height: 0; overflow-y: auto`) while the rest of the body stays in view.
   * Default off: every other dialog keeps its single scrolling body.
   */
  fitBody?: boolean;
  /** Dialog title - rendered in content area; always the dialog's accessible name */
  title?: string;
  /** Footer: config object for standard buttons, ReactNode for custom layout, null to hide */
  footer?: StyledDialogFooterConfig | ReactNode | null;
  /** Dialog content */
  children: ReactNode;
  /** Disable close button (e.g., during loading) */
  closeDisabled?: boolean;
  /** Hide close button entirely (e.g., forced modals with no dismiss) */
  hideClose?: boolean;
  /** Error message rendered flush at the bottom of the dialog, below the footer. */
  error?: ReactNode;
  /** When set, the error band shows a close button calling this. */
  onErrorDismiss?: () => void;
  /**
   * Test id forwarded as `data-testid` on the dialog content root (the element
   * that wraps title, body and footer). Prefer this over passing `data-testid`
   * through `DialogProps` — this prop is typed so callers don't silently drop
   * the attribute by putting it on the component element where TypeScript
   * accepts any `data-*` but the component never reads it.
   */
  testId?: string;
  /**
   * Called when Escape is pressed while this dialog is the top layer. Call
   * `event.preventDefault()` to keep the dialog open (the owner used the key
   * for something inside it: closing a confirm, cancelling a rename).
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /**
   * `data-testid` of an element inside the dialog that takes focus on open, in
   * place of the dialog container (a single-answer prompt: Enter or Space answers it).
   */
  initialFocusTestId?: string;
  /** Radix onCloseAutoFocus on the content element (call preventDefault to take over focus return). */
  onCloseAutoFocus?: (event: Event) => void;
  /** Additional props for the dialog content element */
  DialogProps?: {
    /** If false, dialog sizes to fit its content rather than filling to maxWidth */
    fullWidth?: boolean;
    /** Allow content to overflow the dialog bounds (e.g. for image crop handles) */
    overflow?: 'visible';
    [key: `data-${string}`]: string | undefined;
  };
}

/**
 * The visible title row (icon + text + action) exists only when the title is
 * shown. `hideTitle` and `titleIcon`/`titleAction` are therefore exclusive: the
 * combination is a type error rather than an icon or action silently dropped.
 */
type StyledDialogTitleRowProps =
  | {
      hideTitle?: false;
      /** Icon next to title */
      titleIcon?: ReactNode;
      /** Action rendered right-aligned in the title row (e.g. an "Add" button). */
      titleAction?: ReactNode;
    }
  | {
      /**
       * Render `title` only for screen readers (the visually-hidden Radix
       * Title), in any appearance. For a dialog whose body already shows its
       * own heading: pass that same text as `title` so the dialog is named,
       * without showing it twice (#840). No title row renders, so
       * `titleIcon`/`titleAction` are not accepted.
       */
      hideTitle: true;
      titleIcon?: never;
      titleAction?: never;
    };

export type StyledDialogProps = StyledDialogBaseProps & StyledDialogTitleRowProps;

// =============================================================================
// VARIANT → ACTION MAPPING
// =============================================================================

const VARIANT_ACTION = {
  confirm: Actions.confirm,
  acknowledge: Actions.done,
  destructive: Actions.delete,
} as const;

/** Type guard: is the footer a config object (vs ReactNode)? */
function isFooterConfig(footer: unknown): footer is StyledDialogFooterConfig {
  return (
    typeof footer === 'object' &&
    footer !== null &&
    !isValidElement(footer) &&
    !Array.isArray(footer)
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function StyledDialog({
  open,
  onClose,
  appearance = 'clean',
  size = 'standard',
  nestedSize,
  fitBody = false,
  title,
  hideTitle = false,
  titleIcon,
  titleAction,
  footer,
  children,
  closeDisabled = false,
  hideClose = false,
  error,
  onErrorDismiss,
  testId,
  initialFocusTestId,
  onCloseAutoFocus,
  onEscapeKeyDown,
  DialogProps,
}: StyledDialogProps) {
  const config: DialogAppearanceConfig = dialogAppearance[appearance];
  // Whether the title renders visibly in the content area; otherwise it is
  // rendered only in the visually-hidden Radix Title below.
  const showVisibleTitle = Boolean(config.titleInContent && title && !hideTitle);

  const contentRef = useRef<HTMLDivElement>(null);

  const fullWidth = DialogProps?.fullWidth !== false;
  const overflowVisible = DialogProps?.overflow === 'visible';

  // Extract data-* attributes from DialogProps for the content element
  const dataAttributes: Record<string, string | undefined> = {};
  if (DialogProps) {
    for (const key of Object.keys(DialogProps)) {
      if (key.startsWith('data-')) {
        dataAttributes[key] = DialogProps[key as `data-${string}`];
      }
    }
  }
  // testId prop takes precedence over DialogProps['data-testid']
  if (testId !== undefined) {
    dataAttributes['data-testid'] = testId;
  }

  const showFooter = config.footer.visible && footer !== null && footer !== undefined;
  const handleClose = () => { onClose?.(); };

  // Where this dialog sits in the visual stack: 0 when it's the only one open,
  // higher when it's painted over another. Claimed in a *layout* effect so the
  // re-render lands before the browser paints — a nested dialog must not flash
  // at full width for a frame before shrinking. See `dialogStack.ts` for why
  // this isn't derived from React context.
  const [stackDepth, setStackDepth] = useState(0);
  useLayoutEffect(() => {
    if (!open) {
      setStackDepth(0);
      return;
    }
    const slot = pushOpenDialog();
    setStackDepth(slot.depth);
    return slot.release;
  }, [open]);
  const isNested = stackDepth > 0;

  const contentSized = Boolean(config.contentSized);
  const maxWidth = dialogMaxWidths[isNested && nestedSize ? nestedSize : size];

  // This dialog's own feedback band. Anything a descendant pushes through
  // `useFeedbackZone`, and a mutation's 5xx/transport failure routed by
  // `routeMutationError` (#1174), lands here instead of the global
  // bottom-of-app banner while this dialog is open.
  const layer = useDialogLayerFeedback(open);

  return (
    <DialogLayerProvider value={layer.value} opening={layer.opening}>
    <RadixDialog.Root
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen && !closeDisabled) handleClose(); }}
    >
      <RadixDialog.Portal>
        {/* A stacked dialog paints no scrim of its own: the dialog underneath
            already dimmed the page, and two 50% overlays compound to ~75%,
            which turns the whole screen muddy and dims the parent dialog with
            it. The element still has to exist — Radix hangs click-outside and
            focus containment off it. */}
        <RadixDialog.Overlay
          className={clsx(overlay, config.scrim === 'dim' && overlayDim, isNested && overlayNested)}
          data-nested={isNested || undefined}
          data-scrim={config.scrim}
          data-testid={testIds.dialog.overlay}
        />
        <RadixDialog.Content
          ref={contentRef}
          className={clsx(
            contentBase,
            appearance === 'clean' && contentClean,
            appearance === 'minimal' && contentMinimal,
            contentSized && contentCompact,
            overflowVisible && contentOverflowVisible,
          )}
          data-appearance={contentSized ? appearance : undefined}
          style={{
            ...(!contentSized && {
              maxWidth: fullWidth ? maxWidth : undefined,
              width: fullWidth ? '100%' : 'auto',
            }),
            ...(config.paper.borderRadius && { borderRadius: config.paper.borderRadius }),
            ...(config.paper.maxHeight && { maxHeight: config.paper.maxHeight }),
            ...(config.paper.overflow && !overflowVisible && { overflow: config.paper.overflow }),
          }}
          onInteractOutside={closeDisabled ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={(e) => {
            if (closeDisabled) e.preventDefault();
            else onEscapeKeyDown?.(e);
          }}
          onCloseAutoFocus={onCloseAutoFocus}
          onOpenAutoFocus={(event) => {
            // Default Radix behavior focuses the first focusable descendant. In this dialog
            // that's the close button, which then renders with a focus ring AND its
            // hover/focus tooltip — making every dialog open with the X "highlighted".
            // Move focus to the dialog container itself (tabIndex=-1, no visible ring).
            event.preventDefault();
            const target = initialFocusTestId
              ? contentRef.current?.querySelector<HTMLElement>(`[data-testid="${initialFocusTestId}"]`)
              : null;
            (target ?? contentRef.current)?.focus({ preventScroll: true });
          }}
          {...dataAttributes}
        >
          {/* The dialog's accessible name. Radix owns the wiring: Content gets
              aria-labelledby={Radix's title id} and Dialog.Title stamps that same
              id on its child, so never pass an `id` to the title element or an
              `aria-labelledby` to Content (#840: a second id dangled in the
              minimal appearance and tripped Radix's DialogTitle check in clean).
              When the title is visible in content, Dialog.Title wraps it there
              (asChild). Otherwise render a visually-hidden one; 'Dialog' is a
              last-resort fallback that StyledDialog.title.guard forbids relying on. */}
          {!showVisibleTitle && (
            <RadixDialog.Title asChild>
              <span className={srOnly}>{title ?? 'Dialog'}</span>
            </RadixDialog.Title>
          )}
          <RadixDialog.Description asChild>
            <span className={srOnly}>Dialog content</span>
          </RadixDialog.Description>

          {/* Close button */}
          {!hideClose && !config.header.hideClose && (
            <div className={config.header.isOverlay ? closeButtonOverlayWrapper : closeButtonWrapper}>
              <ActionButton
                action={Actions.close}
                mode="icon-transparent"
                onClick={handleClose}
                disabled={closeDisabled}
                testIdOverride={testIds.dialog.headerCloseButton}
                style={{ color: config.header.color, opacity: 1 }}
              />
            </div>
          )}

          {/* Inner content */}
          <div
            className={clsx(
              !config.content.zeroInnerPadding && innerContent,
              contentSized && innerContentCompact,
              fitBody && innerContentFit,
              !config.content.zeroInnerPadding && config.content.hasExtraTopPadding && innerContentWithTopPadding,
            )}
          >
            {showVisibleTitle && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <RadixDialog.Title asChild>
                  <Text
                    as="h2"
                    variant="cardTitle"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}
                  >
                    {titleIcon && (
                      // Larger than the title text so the icon reads as the dialog's
                      // primary glyph, not an inline character — mirrors the Subheader
                      // module-icon fix (#4098). The icon is an MUI-compat react-icon
                      // that sizes at 1em, so setting font-size on its wrapper scales it.
                      <span style={{ display: 'inline-flex', fontSize: DIALOG_TITLE_ICON_SIZE, flexShrink: 0 }}>
                        {titleIcon}
                      </span>
                    )}
                    {wrapTextChild(title)}
                  </Text>
                </RadixDialog.Title>
                {titleAction}
              </div>
            )}
            {wrapTextChild(children)}
          </div>

          {/* Footer */}
          {showFooter && footer && (
            isFooterConfig(footer) ? (
              <ConfigFooter footer={footer} onClose={handleClose} compact={contentSized} />
            ) : (
              <div className={clsx(footerClass, contentSized && footerCompact)}>{footer}</div>
            )
          )}

          {/* Error band — flush at the dialog bottom, below the footer. */}
          {error && (
            <div className={errorSlot}>
              <Alert
                severity="error"
                onClose={onErrorDismiss}
                style={FLUSH_ALERT_STYLE}
                data-testid={testIds.dialog.errorBand}
              >
                {error}
              </Alert>
            </div>
          )}

          {/* Feedback band — the same slot, fed by descendants that called
              `useFeedbackZone().push`. This is where an error from an action
              taken inside the dialog surfaces, instead of the global banner
              pinned to the bottom of the app behind the overlay. */}
          {layer.items.length > 0 && (
            <div className={errorSlot} data-testid={testIds.dialog.feedbackBand}>
              {layer.items.map((item) => (
                <Alert
                  key={item.id}
                  severity={item.severity}
                  onClose={() => layer.dismiss(item.id)}
                  style={FLUSH_ALERT_STYLE}
                  role="alert"
                  data-testid={testIds.dialog.feedbackItem(item.id)}
                >
                  <span data-testid={testIds.dialog.feedbackItemMessage}>{item.message}</span>
                </Alert>
              ))}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
    </DialogLayerProvider>
  );
}

// =============================================================================
// CONFIG FOOTER (standard primary/secondary buttons)
// =============================================================================

function ConfigFooter({
  footer,
  onClose,
  compact,
}: {
  footer: StyledDialogFooterConfig;
  onClose: () => void;
  compact: boolean;
}) {
  return (
    <div className={clsx(footerClass, compact && footerCompact)}>
      <StyledDialogFooterActions footer={footer} onClose={onClose} />
    </div>
  );
}

/**
 * The config footer's buttons, for a footer NODE rendered by a child component
 * that owns the dialog's action (#821). A node footer sits in the same footer
 * slot, inside this dialog's feedback layer, so
 * `footer={<XActions />}` rendering `<StyledDialogFooterActions footer={…} />`
 * looks exactly like `footer={{…}}` while the mutation behind it answers in
 * this dialog's own band. `onClose` is the secondary button's fallback, as
 * the config footer's is.
 */
export function StyledDialogFooterActions({
  footer,
  onClose,
}: {
  footer: StyledDialogFooterConfig;
  onClose: () => void;
}) {
  const footerVariant = footer.variant ?? 'confirm';
  const footerPrimaryText = footer.primaryText ?? (
    footerVariant === 'confirm' ? buttonLabels.primary.confirm :
    footerVariant === 'destructive' ? buttonLabels.destructive.delete : buttonLabels.primary.ok
  );
  const footerSecondaryText = footer.secondaryText ?? buttonLabels.secondary.cancel;
  const showSecondary = !footer.hideSecondary && footerVariant !== 'acknowledge';

  return (
    <>
      {showSecondary && (
        <ActionButton
          action={Actions.cancel}
          mode="dialog-secondary"
          label={footerSecondaryText}
          onClick={footer.onSecondary ?? onClose}
          disabled={footer.loading}
        />
      )}
      {footer.onPrimary && (
        <ActionButton
          action={VARIANT_ACTION[footerVariant]}
          mode="dialog-primary"
          label={<>{footer.primaryIcon}{footerPrimaryText}</>}
          onClick={footer.onPrimary}
          disabled={footer.loading || footer.primaryDisabled}
          loading={footer.loading}
          loadingLabel={footer.loadingText ?? footerPrimaryText}
        />
      )}
    </>
  );
}
